import { type JSX, type PropsWithChildren, createElement, forwardRef } from 'react';

import {
  gridTemplateAreasVars,
  gridTemplateColumnsVars,
  gridTemplateRowsVars,
  breakpointVariants,
  gridRecipe,
  GridVariants,
} from './Grid.css';
import { GridItem } from './GridItem';
import { DynamicVariantProp } from '../../types';
import { getClassNames } from '../../util/css';
import { extractDynamicStyles } from '../../util/css/vanilla-extract';

const sparkyClassName = 'sparky-grid';

const GridComponent = forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const {
    children,
    as = 'div',
    className = '',
    gridTemplateColumns,
    gridTemplateRows,
    gridTemplateAreas,
    alignY,
    alignX,
    justifyContent,
    flow,
    columns,
    gap,
    rowGap,
    columnGap,
    inline,
  } = props;

  const { classNames: dynamicCssClasses, cssVars } = extractDynamicStyles({
    gridTemplateColumns: [breakpointVariants.gridTemplateColumns, gridTemplateColumnsVars, gridTemplateColumns],
    gridTemplateRows: [breakpointVariants.gridTemplateRows, gridTemplateRowsVars, gridTemplateRows],
    gridTemplateAreas: [breakpointVariants.gridTemplateAreas, gridTemplateAreasVars, gridTemplateAreas],
  });

  const classNames = getClassNames(
    sparkyClassName,
    gridRecipe({
      alignY,
      alignX,
      justifyContent,
      flow,
      columns: columns as GridVariants['columns'],
      gap,
      rowGap,
      columnGap,
      inline,
    }),
    ...dynamicCssClasses,
    className,
  );

  return createElement(as, { className: classNames, style: cssVars, ref }, children);
});

GridComponent.toString = () => `.${sparkyClassName}`;
GridComponent.displayName = 'Grid';

type ColumnNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type ColumnValue = ColumnNumber | `${ColumnNumber}`;

export interface Props extends Omit<GridVariants, 'columns'> {
  /** The amount of columns — each column takes up 1fr. Accepts numbers, numeric strings, or responsive objects. */
  columns?: ColumnValue | { [condition: string]: ColumnValue };
  as?: keyof JSX.IntrinsicElements;
  /** This is the [native grid-template-columns CSS property](https://www.w3schools.com/cssref/pr_grid-template-columns.asp) that will be directly passed on to the grid.   */
  gridTemplateColumns?: DynamicVariantProp<'gridTemplateColumns'>;

  /** This is the [native grid-template-rows CSS property](https://www.w3schools.com/cssref/pr_grid-template-rows.asp) that will be directly passed on to the grid.  */
  gridTemplateRows?: DynamicVariantProp<'gridTemplateRows'>;

  /** This is the [native grid-template-areas CSS property](https://www.w3schools.com/cssref/pr_grid-template-areas.asp) that will be directly passed on to the grid. */
  gridTemplateAreas?: DynamicVariantProp<'gridTemplateAreas'>;

  className?: never;
}

// Compound components with forwardRef cause issues: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-488848720
export const Grid = { ...GridComponent, Item: GridItem } as typeof GridComponent & { Item: typeof GridItem };
